import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MealPlanDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { Section } from '../Section';
import { UsersIcon } from '../icons';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import {
  addGuest,
  attendeeCount,
  attendeesDraftOf,
  GUEST_NAME_MAX,
  GUESTS_MAX,
  removeGuest,
  sameAttendees,
  togglePerson,
  type AttendeesDraft,
} from '../../lib/profiles';

/**
 * Who eats this meal (D32): household people plus named guests. Empty means
 * everyone. Servings follow the headcount unless the box is unticked.
 */
export function PlanAttendeesEditor({ plan }: { plan: MealPlanDto }) {
  const qc = useQueryClient();
  const people = useQuery({
    queryKey: queryKeys.people,
    queryFn: () => api.households.people.list(),
  });
  const saved = attendeesDraftOf(plan);
  const [draft, setDraft] = useState<AttendeesDraft>(saved);
  const [guest, setGuest] = useState('');
  const [syncServings, setSyncServings] = useState(true);

  // Re-seed when the server copy changes (another tab, or after saving).
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(JSON.parse(savedKey) as AttendeesDraft);
  }, [savedKey]);

  const save = useMutation({
    mutationFn: (d: AttendeesDraft) =>
      api.plans.setAttendees(plan.id, {
        personIds: d.personIds,
        guests: d.guests,
        syncServings,
      }),
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(queryKeys.plans.detail(plan.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
  });

  const dirty = !sameAttendees(draft, saved);
  const count = attendeeCount(draft);
  const list = people.data?.people ?? [];

  function onAddGuest(e: FormEvent) {
    e.preventDefault();
    save.reset();
    setDraft(addGuest(draft, guest));
    setGuest('');
  }

  return (
    <Section
      title="Who’s eating"
      accent="orange"
      icon={<UsersIcon className="size-5" />}
      description="Allergy, diet and dislike checks use these people. Pick nobody for everyone."
    >
      {people.isError ? <Alert tone="error">{messageForError(people.error)}</Alert> : null}
      <ul className="flex flex-wrap gap-2" aria-label="Household people">
        {list.map((p) => {
          const on = draft.personIds.includes(p.id);
          return (
            <li key={p.id}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => {
                  save.reset();
                  setDraft(togglePerson(draft, p.id));
                }}
                className={`min-h-11 rounded-full border px-3.5 text-sm ${
                  on ? 'border-brand bg-brand text-white' : 'border-line bg-surface text-ink'
                }`}
              >
                {on ? <span aria-hidden>✓ </span> : null}
                {p.name}
              </button>
            </li>
          );
        })}
        {draft.guests.map((g, i) => (
          <li
            key={`${g}-${i}`}
            className="inline-flex min-h-11 items-center gap-1 rounded-full border border-orange/40 bg-orange-soft pl-3.5 text-sm text-orange-ink"
          >
            {g} <span className="text-xs">(guest)</span>
            <button
              type="button"
              aria-label={`Remove guest ${g}`}
              onClick={() => {
                save.reset();
                setDraft(removeGuest(draft, i));
              }}
              className="inline-flex size-11 items-center justify-center rounded-full"
            >
              <span aria-hidden>×</span>
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={onAddGuest} noValidate className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            id={`plan-guest-${plan.id}`}
            label="Add a guest"
            placeholder="e.g. Grandma"
            value={guest}
            maxLength={GUEST_NAME_MAX}
            onChange={(e) => setGuest(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          block={false}
          disabled={guest.trim() === '' || draft.guests.length >= GUESTS_MAX}
        >
          Add
        </Button>
      </form>
      <label className="inline-flex min-h-11 items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={syncServings}
          onChange={(e) => setSyncServings(e.target.checked)}
          className="size-5 accent-brand"
        />
        {count > 0 ? `Set servings to ${count}` : 'Set servings to the household size'}
      </label>
      {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
      {save.isSuccess && !dirty ? <Alert tone="success">Saved who’s eating.</Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          block={false}
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(draft)}
        >
          Save who’s eating
        </Button>
        {dirty ? (
          <Button variant="ghost" block={false} onClick={() => setDraft(saved)}>
            Reset
          </Button>
        ) : null}
      </div>
    </Section>
  );
}
